/**
 * Chart - a thin, theme-aware wrapper over an ApexCharts instance.
 *
 * The page writes only data + the bits it cares about; the theme layer (colors,
 * fonts, axis/grid, tooltip) comes from the live CSS tokens and is re-applied
 * automatically on theme switch (see index.js -> the global observer).
 */
import { themeOptions } from './theme.js';

// Every live chart, so the global theme observer can re-theme them all.
const registry = new Set();
let reduceMotion = false;

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

// Deep-merge (objects merged, arrays + scalars replaced) - `over` wins. Returns
// a fresh object so neither input is mutated.
function merge(base, over) {
	const out = Array.isArray(base) ? base.slice() : { ...base };
	for (const key of Object.keys(over || {})) {
		out[key] = isPlainObject(over[key]) && isPlainObject(out[key]) ? merge(out[key], over[key]) : over[key];
	}
	return out;
}

export class Chart {
	constructor(el, config) {
		this.el = el;
		this.config = config;                 // the page's config, kept for re-theming
		this.type = config.chart && config.chart.type;
		this.apex = new ApexCharts(el, merge(themeOptions(reduceMotion, this.type), config));
		this.apex.render();
		el.genusChart = this;
		registry.add(this);
	}

	// Recompute the theme layer and push only its visual options (never the
	// series data, so a theme flip doesn't re-animate the values).
	applyTheme() {
		const options = merge(themeOptions(reduceMotion, this.type), this.config);
		delete options.series;
		this.apex.updateOptions(options, false, false);
	}

	// Merge new options into the page config and re-apply (theme + config).
	update(options) {
		this.config = merge(this.config, options || {});
		this.type = this.config.chart && this.config.chart.type;
		return this.apex.updateOptions(merge(themeOptions(reduceMotion, this.type), this.config), false, true);
	}

	// Replace just the data series (keeps the current options).
	setSeries(series) {
		this.config = merge(this.config, { series });
		return this.apex.updateSeries(series, true);
	}

	destroy() {
		registry.delete(this);
		this.apex.destroy();
		delete this.el.genusChart;
	}
}

export const refreshAllThemes = () => registry.forEach((c) => c.applyTheme());
export const setReduceMotion = (v) => { reduceMotion = !!v; };
